import {
	Fragment,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import classNames from "classnames";
import {
	AppIcon,
	DropOverActionList,
	IconButton
} from "client/Components";
import {
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import styles from "./IconCard.module.less";
import { useCallbackWithoutArguments, useToggle } from "client/Hooks";
import { Action, reduceToAvailableActions } from "client_legacy/ndaw/actions";
import { actionComponentFactory } from "client/Components/ActionList/actionsRenderHelpers";
import { AppImageDisplayInfo } from "client/Api/AppIcon/common";
import { AppColorViewModel } from "client/Api/Apps/common";
import { getReactColorAttributes } from "client_legacy/ndaw/appColor";

export type IconCardHandle = {
	scrollIntoView: () => void;
};

export type IconCardWithoutActions = {};

export type IconCardWithActions<TContext> = {
	actions: readonly Action<TContext>[];
	actionContext: TContext;
};

export type IconCardProps<TContext> = {
	name: string;
	icon: AppImageDisplayInfo;
	iconBackgroundColor?: AppColorViewModel;
	isSelected: boolean;
	onSelect: () => void;
	trackingId: string;
	ref?: Ref<IconCardHandle>;
} & (
		| IconCardWithoutActions
		| IconCardWithActions<TContext>
	);

export const IconCard = <TContext,>(props: IconCardProps<TContext>): ReactElement => {
	const {
		name,
		icon,
		iconBackgroundColor,
		isSelected,
		onSelect: onSelectFromProps,
		trackingId,
		ref: forwardedRef
	} = props;

	const onSelect: () => void = useCallbackWithoutArguments(onSelectFromProps);

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const { colorClass, colorStyle } = isNotNil(iconBackgroundColor)
		? getReactColorAttributes(iconBackgroundColor)
		: { colorClass: undefined, colorStyle: undefined };

	useImperativeHandle(
		forwardedRef,
		(): IconCardHandle => ({
			scrollIntoView: () => {
				ref.current
					?.scrollIntoView();
			}
		}),
		[]
	);

	return (
		<>
			<div
				{...setTrackingId(trackingId)}
				ref={ref}
				className={classNames(
					styles.root,
					{ [styles.root_active]: isSelected }
				)}
				onClick={onSelect}
				title={name}
			>
				{hasActions(props) && (
					<IconCardActions<TContext>
						{...props}
						name={name}
					/>
				)}

				<div
					className={classNames(
						styles.icon,
						colorClass,
						{ [styles.icon_appColor]: isNotNil(iconBackgroundColor) }
					)}
					style={colorStyle}
				>
					<AppIcon
						value={icon}
						color="inherit"
					/>
				</div>
				<div className={styles.name}>
					{name}
				</div>
			</div>
		</>
	);
};
IconCard.displayName = "IconCard";

type IconCardActionsProps<TContext> = IconCardWithActions<TContext> & {
	name: string;
};

const IconCardActions = <TContext,>(props: IconCardActionsProps<TContext>): ReactElement | null => {
	const {
		name
	} = props;

	const [isOpen, toggleIsOpen] = useToggle();
	const ref: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const availableActions = useMemo(
		(): IconCardWithActions<TContext> | undefined => {
			const { actions, actionContext } = props;
			const availableActions: readonly Action<TContext>[] = reduceToAvailableActions(
				actions,
				actionContext
			);

			if (availableActions.length === 0) {
				return undefined;
			}

			return {
				actions: availableActions,
				actionContext
			};
		},
		[props]
	);

	if (!availableActions) {
		return null;
	}

	return (
		<div
			className={classNames(
				styles.actions,
				{ [styles.actions_isOpen]: isOpen }
			)}
		>
			<IconButton
				trackingId="actions"
				icon="ellipsis"
				kind="transparent-colored"
				onClick={() => toggleIsOpen()}
				ref={ref}
				bubbleClick={false}
				shape="round"
				isActive={isOpen}
			/>

			{isOpen && ref.current && (
				<DropOverActionList
					trackingId={generateTrackingId("actions", "__dropover")}
					target={ref.current}
					targetAlignment="br"
					contentAlignment="tr"
					onClose={(): void => toggleIsOpen(false)}
					bubbleClick={false}
					bubbleContextMenu={false}
					menuTitle={name}
				>
					{availableActions.actions
						.map(
							(x): ReactElement => (
								<Fragment key={x.name}>
									{actionComponentFactory(x, availableActions.actionContext)}
								</Fragment>
							)
						)
					}
				</DropOverActionList>
			)}
		</div>
	);
};
IconCardActions.displayName = "IconCardActions";

function hasActions<TContext>(
	props: IconCardWithoutActions | IconCardWithActions<TContext>
): props is IconCardWithActions<TContext> {
	return isNotNil((props as IconCardWithActions<TContext>).actions);
}
